import React, { useContext } from "react";

// Import Contexts
import { LanguageContext } from "contexts";

// Import Components
import {
  TitleBar,
  PrimaryButton,
  NormalText,
  Loading,
  Visualization,
} from "components/index";
import { Language, ProcedureType } from "api/type";
import { TPostTransformsBatchResponse } from "api/clinicos";
import { adjustTextMap, adjustTextMapSpanish } from "api/constants";
import { isAISupportedProcedure } from "utils/isAISuportedProcedureAndArea";
import { getTranslation } from "utils/translate";

type ScreenVisualizeProps = {
  selectedProcedure: ProcedureType | null;
  postTransformsBatchResponse: TPostTransformsBatchResponse | null;
};

const ScreenVisualize: React.FC<ScreenVisualizeProps> = ({
  selectedProcedure,
  postTransformsBatchResponse,
}) => {
  // Context
  const language = useContext<Language>(LanguageContext)[0];

  const renderVisualization = () => {
    if (!selectedProcedure) return;
    if (!postTransformsBatchResponse) {
      return (
        <div
          style={{
            padding: "50px",
          }}
        >
          <Loading />
        </div>
      );
    }
    if (postTransformsBatchResponse) {
      if (!postTransformsBatchResponse.response[0].metadata.success) {
        return (
          <NormalText
            textContent={
              postTransformsBatchResponse.response[0].metadata.reason
            }
          />
        );
      }
      return (
        <Visualization
          afterImageUrls={
            postTransformsBatchResponse.response[0].presigned_urls
          }
          procedureDisplayMessage={
            language === "es"
              ? adjustTextMapSpanish[selectedProcedure]
              : adjustTextMap[selectedProcedure]
          }
          metaData={postTransformsBatchResponse.response[0].metadata}
        />
      );
    }
  };

  if (!selectedProcedure) return null;

  return (
    <div className="screen-container">
      <TitleBar
        name={getTranslation("instantBeforeAfter", language)}
        onClickBackButton={null}
        centerTitle
        showLogo={false}
      />
      {isAISupportedProcedure(selectedProcedure) ? (
        <div style={styles.visualizationContainer}>{renderVisualization()}</div>
      ) : (
        <NormalText
          textContent={getTranslation(
            "theProcedureIsCurrenlyNotSupportedByAI",
            language,
          )}
          style={{ marginBottom: "auto" }}
        />
      )}

      <PrimaryButton
        onClick={() => {
          window.parent.postMessage({ message: "close modal" }, "*");
        }}
        disabled={
          !postTransformsBatchResponse &&
          isAISupportedProcedure(selectedProcedure)
        }
        fullWidth
        text={getTranslation("done", language)}
      />
    </div>
  );
};
const styles = {
  visualizationContainer: {
    width: "80%",
    height: "100%",
    margin: "auto",
  },
  loading: {
    margin: "50px auto",
  },
  errorText: {
    color: "red",
    textAlign: "center",
    margin: "50px auto",
  },
} as const;

export default ScreenVisualize;
